import React from 'react'
import { connect } from 'react-redux'
import { Badge, Box, Button, IconButton, Typography } from '@mui/material'
import {
  Close, Pause, PlayArrow, RadioButtonUnchecked, Restore
} from '@mui/icons-material'
import classNames from 'classnames'
import {
  sendWs, CORRECT, WRONG, THROUGH, RESET, UNDO, REDO, TOGGLE_TIMER, RESET_TIMER
} from '../../lib/send'
import { rxGamepad } from '../../lib/rx_subject'
import { gamepadButtonName } from '../../lib/gamepad'
import { clearEditBoards } from '../../redux/actions/board'
import './Actions.scss'

const Master = ({ className, hidden, setting: { personal: { bind } }, timer, rule, clearEditBoards }) => {
  const [focus, setFocus] = React.useState(false)

  const subscription = React.useRef(null)

  const onCorrect = keepRight => { sendWs(CORRECT, { keepRight }) }
  const onWrong = keepRight => { sendWs(WRONG, { keepRight }) }

  const onThrough = () => {
    clearEditBoards()
    sendWs(THROUGH)
  }

  const onReset = () => {
    clearEditBoards()
    sendWs(RESET)
  }

  const onUndo = () => { sendWs(UNDO) }
  const onRedo = () => { sendWs(REDO) }

  const toggleTimer = () => {
    sendWs(TOGGLE_TIMER)
  }

  const resetTimer = () => {
    sendWs(RESET_TIMER)
  }

  const onKeyDown = evt => {
    switch (evt.keyCode) {
      case 81: // q
        onCorrect(evt.shiftKey)
        break
      case 87: // w
        onWrong(evt.shiftKey)
        break
      case 69: // e
        onThrough()
        break
      case 82: // r
        onReset()
        break
      case 37: // <-
        onUndo()
        break
      case 39: // ->
        onRedo()
        break
      case 90: // z
        if (rule.other.timer.active) {
          toggleTimer()
        }
        break
      case 67: // c
        if (rule.other.timer.active) {
          resetTimer()
        }
        break
      default:
        break
    }
  }

  const onFocus = evt => {
    if (evt.target.className.split(' ').includes('actions-content')) {
      setFocus(true)
    }
    subscription.current = rxGamepad.subscribe(button => {
      if (button === bind.gamepad.correct) {
        onCorrect(false)
      }
      if (button === bind.gamepad.wrong) {
        onWrong(false)
      }
      if (button === bind.gamepad.through) {
        onThrough()
      }
      if (button === bind.gamepad.reset) {
        onReset()
      }
      if (button === bind.gamepad.undo) {
        onUndo()
      }
      if (button === bind.gamepad.redo) {
        onRedo()
      }
    })
  }

  const onBlur = evt => {
    if (evt.target.className.split(' ').includes('actions-content')) {
      setFocus(false)
    }
    if (subscription.current) {
      subscription.current.unsubscribe()
    }
  }

  const timerButtons = (
    <>
      <Typography className="title">
        タイマー
      </Typography>
      <Badge color="primary" overlap="circular"
             badgeContent="z" invisible={!focus}>
        <IconButton onClick={toggleTimer}>
          {!timer.running ? <PlayArrow /> : <Pause />}
        </IconButton>
      </Badge>
      <Badge color={!timer.running ? 'primary' : 'mono'} overlap="circular"
             badgeContent="c" invisible={!focus}>
        <IconButton disabled={timer.running} onClick={resetTimer}>
          <Restore />
        </IconButton>
      </Badge>
      <Box className="splitter" />
    </>
  )

  return (
    <Box className={classNames(className, 'master-actions', { hidden })}
         tabIndex="0" onKeyDown={onKeyDown} onFocus={onFocus} onBlur={onBlur}>
      <Box className="content">
        {rule.other.timer.active && timerButtons}
        <Badge color="primary" overlap="circular"
               badgeContent="q" invisible={!focus}>
          <Badge color="secondary" overlap="circular"
                 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                 badgeContent={gamepadButtonName(bind.gamepad.correct)}
                 invisible={!focus || bind.gamepad.correct === null}>
            <Button variant="outlined" size="large"
                    color="primary" className="correct-button"
                    onClick={evt => onCorrect(evt.shiftKey)}
                    startIcon={<RadioButtonUnchecked />}>
              正解
            </Button>
          </Badge>
        </Badge>
        <Badge color="primary" overlap="circular"
               badgeContent="w" invisible={!focus}>
          <Badge color="secondary" overlap="circular"
                 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                 badgeContent={gamepadButtonName(bind.gamepad.wrong)}
                 invisible={!focus || bind.gamepad.wrong === null}>
            <Button variant="outlined" size="large"
                    color="secondary" className="wrong-button"
                    onClick={evt => onWrong(evt.shiftKey)}
                    startIcon={<Close />}>
              誤答
            </Button>
          </Badge>
        </Badge>
        <Badge color="primary" overlap="circular"
               badgeContent="e" invisible={!focus}>
          <Badge color="secondary" overlap="circular"
                 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                 badgeContent={gamepadButtonName(bind.gamepad.through)}
                 invisible={!focus || bind.gamepad.through === null}>
            <Button variant="outlined" size="large"
                    color="mono" className="through-button"
                    onClick={onThrough}>
              次の問題
            </Button>
          </Badge>
        </Badge>
        <Badge color="primary" overlap="circular"
               badgeContent="r" invisible={!focus}>
          <Badge color="secondary" overlap="circular"
                 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                 badgeContent={gamepadButtonName(bind.gamepad.reset)}
                 invisible={!focus || bind.gamepad.reset === null}>
            <Button variant="outlined" size="large"
                    color="mono" className="reset-button"
                    onClick={onReset}>
              リセット
            </Button>
          </Badge>
        </Badge>
        <Badge color="primary" overlap="circular"
               badgeContent="&#x2190;" invisible={!focus}>
          <Badge color="secondary" overlap="circular"
                 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                 badgeContent={gamepadButtonName(bind.gamepad.undo)}
                 invisible={!focus || bind.gamepad.undo === null}>
            <Button variant="outlined" size="large"
                    color="mono" className="undo-button"
                    onClick={onUndo}>
              Undo
            </Button>
          </Badge>
        </Badge>
        <Badge color="primary" overlap="circular"
               badgeContent="&#x2192;" invisible={!focus}>
          <Badge color="secondary" overlap="circular"
                 anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                 badgeContent={gamepadButtonName(bind.gamepad.redo)}
                 invisible={!focus || bind.gamepad.redo === null}>
            <Button variant="outlined" size="large"
                    color="mono" className="redo-button"
                    onClick={onRedo}>
              Redo
            </Button>
          </Badge>
        </Badge>
      </Box>
    </Box>
  )
}

export default connect(
  state => ({
    setting: state.setting,
    timer: state.timer,
    rule: state.rule
  }),
  dispatch => ({
    clearEditBoards: () => dispatch(clearEditBoards())
  })
)(Master)
